import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { useVoiceInput } from '@/hooks/useVoiceInput';

export interface VoiceButtonProps {
  onTranscriptChange?: (transcript: string) => void;
  onFinalTranscript?: (transcript: string) => void;
  className?: string;
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export const VoiceButton = React.forwardRef<HTMLButtonElement, VoiceButtonProps>(
  ({ onTranscriptChange, onFinalTranscript, className, size = 'icon' }, ref) => {
    const {
      isListening,
      isSupported,
      permissionDenied,
      startListening,
      stopListening,
    } = useVoiceInput({
      onTranscriptChange,
      onFinalTranscript,
      silenceTimeout: 3000,
    });

    // Don't render if Web Speech API is not supported
    if (!isSupported) {
      return null;
    }

    // Check if we're on HTTPS or localhost
    const isSecureContext = window.location.protocol === 'https:' || window.location.hostname === 'localhost';

    const handleClick = async () => {
      if (isListening) {
        stopListening();
      } else {
        await startListening();
      }
    };

    return (
      <div className="relative">
        <Button
          ref={ref}
          type="button"
          variant="ghost"
          size={size}
          onClick={handleClick}
          disabled={!isSecureContext || permissionDenied}
          className={cn(
            'relative',
            isListening && 'text-error',
            className
          )}
          aria-label={isListening ? 'Stop recording' : 'Start voice input'}
          title={
            !isSecureContext
              ? 'Voice input requires HTTPS'
              : permissionDenied
              ? 'Microphone permission denied'
              : isListening
              ? 'Stop recording'
              : 'Start voice input'
          }
        >
          {/* Microphone icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
          >
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" x2="12" y1="19" y2="22" />
          </svg>

          {/* Listening indicator pulse */}
          {isListening && (
            <span className="absolute inset-0 animate-ping rounded-full bg-red-400 opacity-75" />
          )}
        </Button>

        {/* HTTPS warning */}
        {!isSecureContext && (
          <div className="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 dark:bg-gray-700">
            Requires HTTPS
          </div>
        )}
      </div>
    );
  }
);

VoiceButton.displayName = 'VoiceButton';
